import React, { Component, useState, useEffect } from 'react';
import Navbar from '../Navbar';
import ReactMarkdown from 'react-markdown';
import Code from './Code';
import Citation, {addCiteIndex, makeCitationsDict} from './Citation';
// import rehypeHighlight from 'rehype-highlight'
import remarkMath from 'remark-math';  // Markdown math
import rehypeKatex from 'rehype-katex';// Renders math in HTML
import rehypeRaw from 'rehype-raw';    // Allows custom HTML tags in markdown
// import stringify from 'rehype-stringify';
// import stringify, { rehypeStringify } from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import { navbarHeight } from '../../App';
import { visit } from 'unist-util-visit';  // Import visit
// import { stringify } from 'unist-util-stringify';
import { unified } from 'unified';
import {toMarkdown} from 'mdast-util-to-markdown'
import {gfmFootnoteFromMarkdown, gfmFootnoteToMarkdown} from 'mdast-util-gfm-footnote'
import { remark } from 'remark';
import remarkHtml from 'remark-html';
import { makeBlogPostURL } from '../makeURL';
import { calcReadingTime } from '../utils';
import ReactGA from 'react-ga4';


import 'katex/dist/katex.min.css';
import ProjectsHeader from '../ProjectsHeader';
import HeadingRenderer, {headingTextToSlug} from './BlogPostHeadings';
import TableOfContents from './TableOfContents';
import HrSeperator from './HrSeperator';
import * as blogTagColors from '../../content/blogTagColors.json';
import FootnoteLabel, { addFootnoteIndex, getFootnoteLabel } from './FootnoteLabel';
import Footer from '../Footer';


function parseCiteTags(markdownString) {
  // Function that replaces occurrences of substrings.

  // CITATIONS
  // Replace "\cite{NAME}" or "[@NAME]" with "<cite a='NAME'></cite>". This allows writing citations in LaTeX style.
  // This also removed optional leading whitespaces before "\cite{...}".
  markdownString = markdownString.replace(/\s*\\cite{([^}]+)}/g, "<cite a='$1'></cite>");
  markdownString = markdownString.replace(/\s*\[\@(.+?)\]/g, "<cite a='$1'></cite>");;

  // LINKS
  // Replace substrings "\href{URL}{NAME}" with "[NAME](URL)".
  markdownString = markdownString.replace(/\\href{([^}]+)}{([^}]+)}/g, "[$2]($1)");

  return markdownString;
}

function makeCiteMeString(blogPostDict) {
  const title = blogPostDict.title;
  const finishDate = blogPostDict.finishDate;
  const year = finishDate.toLocaleDateString('en-US', {year: 'numeric'})
  
  return `@article{wulff${year}${title.split(/[\s,-:]+/)[0].toLowerCase()},
    title   = "${title}",
    author  = "Wulff, Philipp",
    journal = "philippwulff.github.io",
    year    = "${year}",
    month   = "${finishDate.toLocaleDateString('en-US', {month: 'short'})}",
    url     = "philippwulff.github.io${makeBlogPostURL(title)}"
  }`
}

const BlogPost = ( { blogPostDict, theme, setTheme, className } ) => {

  ReactGA.send({ hitType: "pageview", page: "/blog/blogPost", title: `BlogPost: ${blogPostDict.title}` });

  let finishDate = blogPostDict.finishDate || undefined;
  if (finishDate) {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    finishDate = finishDate.toLocaleDateString('en-US', options);
  }

  const [bibtexDict, setBibtexDict] = useState({});
  const [markdownContent, setText] = useState('');
  const [footnotes, setFootnotes] = useState([]);
  const [headings, setHeadings] = useState([]);
  const [readingTime, setReadingTime] = useState(0);

  useEffect(() => {
    async function fetchReadingTime() {
      const readingTime = await calcReadingTime(blogPostDict.markdownFile);
      setReadingTime(readingTime);
    }
    fetchReadingTime();
  }, [])

  const parser = new DOMParser();

  useEffect(() => {
      // FETCH THE BIBTEX
      makeCitationsDict(blogPostDict.citations).then(citationsDict => {
        setBibtexDict(citationsDict);
      })
      .catch(error => {
        // Handle any errors that occurred during the citation generation
        console.error(error);
      });

      // FETCH THE MARKDOWN FILE
      fetch(blogPostDict.markdownFile)
      .then((response) => response.text())
      .then((md) => {
          md = parseCiteTags(md);
          setText(md);
          const markdownAST = remark().use(remarkGfm).parse(md);

          // Extract headings from markdown
          const allHeadings = [];
          visit(markdownAST, 'heading', (node) => {
            // Ignore H1
            // if ( node.depth > 1 ) {
              allHeadings.push({
                level: node.depth - 1, 
                // The heading text in the markdown format without the leading "#".
                text: node.children[0].value,
                // Set a linkable ID for this heading (such that the TOC can link to it).
                id: headingTextToSlug(node.children[0].value),
              });
            // };
          });
          setHeadings(allHeadings);

          // Extract footnote definitions from markdown
          // const allFootnotes = {};
          // visit(markdownAST, 'footnoteDefinition', (node) => {
          //   const htmlString = remark()
          //     .use(remarkGfm)
          //     .use(remarkHtml)
          //     .stringify({ type: 'root', children: node.children });
          //   const doc = parser.parseFromString(htmlString, 'text/html');
          //   const element = doc.body.firstChild;
          //   allFootnotes[node.identifier] = element;
          // });
          // setFootnotes(allFootnotes);
          var regex = /<footnote>(.*?)<\/footnote>/g;
          var matches = [];
          var match;
          while (match = regex.exec(md)) {
            matches.push(match[1]);
          }
          setFootnotes(matches);
      }) 
  }, [blogPostDict]);

  const innerContentWidth = "max-w-xl xl:max-w-2xl sm:mx-auto w-full";
  const outerContentWidth = "mx-5 sm:mx-auto max-w-xl lg:max-w-7xl";

  return (
    <div className={`bg-light dark:bg-dark overflow-hidden ${className}`} style={{marginTop: navbarHeight}}>
      <Navbar theme={theme} setTheme={setTheme}/>
      <ProjectsHeader title={blogPostDict.title} subtitle={blogPostDict.description}/>
      <HrSeperator/>
      {/* Blog Post Meta Data */}
      <div className={`flex flex-row mt-5 mb-5 justify-between text-xs sm:text-sm text-dark dark:text-light ${innerContentWidth}`}>
        <div className='flex flex-col ml-5'>
          <span className='font-thin'>AUTHORS</span>
          <ul>
            { blogPostDict.authors.map((author, idx) => {
                return <li key={idx}>{author}</li>;
            })}
          </ul>
        </div>
        <div className='flex flex-col ml-1'>
          <span className='font-thin'>PUBLISHED</span>
          <span>{finishDate}</span>
        </div>
        <div className='flex flex-col ml-1'>
          <span className='font-thin'>READING TIME</span>
          <span>{readingTime} min</span>
        </div>
        <div className='flex flex-col ml-1 mr-5'>
          <span className='font-thin'>TAGS</span>
          { blogPostDict.tags.map((item, idx) => {
              if ( !(item in blogTagColors) ) { console.error(`No color for tag '${item}' found in content/blogTagColors.json!`) };
              return <span key={idx} className='rounded text-center font-bold pl-1 pr-1 mr-0.5 mb-0.5' style={{backgroundColor: blogTagColors[item]}}>{item}</span>;
          })}
        </div>
      </div>
      <HrSeperator/>

      <div className={`relative flex flex-col lg:flex-row ${outerContentWidth}`}>
        {/* TOC */}
        <div className='lg:absolute lg:w-1/5 mt-5 md:mt-10'>
          <TableOfContents headings={headings}/>
        </div>
        {/* Blog Post Text */}
        <div className={`${innerContentWidth}`}>
          <ReactMarkdown 
            children={markdownContent} 
            className='text-left text-md md:text-base mt-10 text-dark dark:text-light'
            components={{
              code: Code,
              cite: ({children, node}) => <Citation children={children} node={node} bibtexDict={bibtexDict}/>,
              h1: HeadingRenderer,
              h2: HeadingRenderer,
              h3: HeadingRenderer,
              h4: HeadingRenderer,
              h5: HeadingRenderer,
              h6: HeadingRenderer,
              hr: HrSeperator,
              blockquote: ({children}) => <blockquote className="pl-4 border-l-8 mt-1 mb-2 border-theme-orange">{children}</blockquote>,
              table: ({children}) => <div className='overflow-x-scroll'><table className='w-full mt-3 mb-3'>{children}</table></div>,
              th: ({children}) => <th className='border-b border-black border-opacity-50 px-2'>{children}</th>,
              td: ({children}) => <td className='border-t border-black border-opacity-10 px-2'>{children}</td>,
              ol: ({children}) => <ol className='list-decimal ml-8 mb-2'>{children}</ol>,
              ul: ({children}) => <ul className='list-disc ml-8 mb-2'>{children}</ul>,
              a: ({children, ...props}) => <a {...props} target='_blank' className="underline hover:cursor-pointer">{children}</a>,
              div: ({children, className, ...props}) => (
                <div 
                  children={children} 
                  className={`${className.includes('math') ? 'text-xs sm:text-base' : ''} ${className}`} 
                  {...props}
                />),
              // This ignors the "Footnotes" section (because there is the custom version below)
              section: ({children}) => (
                (children[0] != undefined && children[0].props != undefined && children[0].props.id === "footnote-label") ? 
                null : <section children={children}/>
              ),
              // Class is relative because most things are positioned in paragraphs.
              p: ({children}) => <p className='mb-2'>{children}</p>,
              img: ({children, src, alt}) => {
                src = blogPostDict.parentDirName + "/" + src.replace("./", "");
                return <img 
                  src={require(`../../content/blogPosts/${src}`)} 
                  children={children} 
                  alt={alt}
                  className='rounded-xl overflow-hidden mt-5 mx-auto'
              />},
              // Using <div> in <mycaption> raises a warning, since <div> is not allowed in <p>. 
              // But it auto-closes the previous <p> tag and therefore works as expected.
              mycaption: ({children}) => <div className='text-dark-accent dark:text-light-accent text-sm p-2 mb-5'>{children}</div>,    
              footnote: ({children, node}) => <FootnoteLabel children={children} node={node}/>
            }}
            // Remark parses markdown into an abstract syntax tree (AST). 
            remarkPlugins={[remarkMath, remarkGfm]}  // Modify the AST in some way.
            // Rehype transforms the markdown AST to an HTML AST.
            rehypePlugins={[rehypeKatex, rehypeRaw, addCiteIndex, addFootnoteIndex]} // Modify the HTML AST.
          />
        </div>  
      </div>
      <HrSeperator/>
      {/* FOOTNOTES */}
      <div className='text-xs text-dark-accent dark:text-light-accent'>
        { footnotes.length > 0 ? 
          <div className={`relative flex flex-col lg:flex-row mt-5 ${outerContentWidth}`}>
              <h2 className="lg:absolute lg:w-1/5 lg:mx-2 lg:ml-20 font-semibold text-lg mb-3 text-dark dark:text-light-accent2">Footnotes</h2>
              <div className={`text-xs text-left ml-6 ${innerContentWidth}`}>
                <ol className='list-decimal'>
                  {
                    footnotes.map((footnote, idx) => <li key={idx}>
                        <ReactMarkdown 
                          children={footnote+'<end/>'} // a trick to append a link to the end of the string
                          components={{
                            end: () => <span>
                              &nbsp;<a href={`#${getFootnoteLabel(idx+1)}`} aria-label="Back to content" className='border border-theme-turquoise dark:border-theme-orange'>↩</a>
                              </span>,
                            a: ({children, ...props}) => <a {...props} target='_blank' className="underline hover:cursor-pointer">{children}</a>,
                          }}
                          remarkPlugins={[remarkMath, remarkGfm]} 
                          rehypePlugins={[rehypeKatex, rehypeRaw]}
                        />
                    </li>)
                  }
                </ol>
              </div>
          </div>
          : null 
        }
        {/* REFERENCES */}
        { Object.keys(bibtexDict).length > 0 ? 
          <div className={`relative flex flex-col lg:flex-row mt-5 ${outerContentWidth}`}>
            <h2 className="lg:absolute lg:w-1/5 lg:mx-2 lg:ml-20 font-semibold text-lg mb-3 text-dark dark:text-light-accent2">References</h2>
            <div className={`text-xs text-left ml-6 ${innerContentWidth}`}>
              <ol className='list-decimal'>
                {
                  Object.entries(bibtexDict).map(([key, value]) => <li key={key}><div dangerouslySetInnerHTML={{ __html: value }}/></li>)
                }
              </ol>
            </div>
          </div>
          : null
        }
        {/* CITE ME */}
        <div className={`relative flex flex-col lg:flex-row mt-5 ${outerContentWidth}`}>
          <h2 className="lg:absolute lg:w-1/5 lg:mx-2 lg:ml-20 font-semibold text-lg mb-3 text-dark dark:text-light-accent2">Citation</h2>
          <div className={`text-xs ${innerContentWidth}`}>
            <Code language='latex'>{makeCiteMeString(blogPostDict)}</Code>
          </div>
        </div>
      </div>
      <Footer/>
    </div>  
  )
}

export default BlogPost
